import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { EmailRequestType, LabelId } from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import {
  useInboxEntriesStore,
  useUserCommunicationsStore,
} from '../../../store';
import { getCurrentClerkUserId } from '../../../utils/getCurrentClerkUserId';
import { isOwnMailboxCommunication } from '../../../utils/userCommunicationHelpers';
import { useCustomToast } from '../../useCustomToast';

export const useMarkAsReadUserCommunication = () => {
  const { t } = useTranslation();

  const { errorMessage } = useCustomToast();
  const handleMarkAsReadUserCommunication = useCallback(
    async (messageIds: string | string[], onSuccess?: () => void) => {
      const {
        readUserCommunication,
        unReadUserCommunication,
        setMarkAsReadLoading,
        getUserCommunicationByMessageId,
      } = useUserCommunicationsStore.getState();
      const {
        readUserCommunicationsInInboxEntries,
        unreadUserCommunicationsInInboxEntries,
      } = useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }
      const validCommunications = messageIdsArray.filter((messageId) => {
        const userCommunication = getUserCommunicationByMessageId(messageId);
        if (!userCommunication) {
          log.error(`UserCommunication with messageId ${messageId} not found`);
          return false;
        }
        return isOwnMailboxCommunication(
          userCommunication,
          getCurrentClerkUserId(),
        );
      });

      if (validCommunications.length === 0) {
        errorMessage(t('inbox.userCommunication.actions.markAsReadError'));
        return;
      }

      validCommunications.forEach((id) => {
        const comm = getUserCommunicationByMessageId(id);
        if (comm?.labelIds?.includes(LabelId.UNREAD)) {
          readUserCommunication(id);
        }
      });

      readUserCommunicationsInInboxEntries(validCommunications);
      onSuccess?.();
      setMarkAsReadLoading(true);

      try {
        const operations = validCommunications.map(async (messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);

          if (!userCommunication) {
            return { messageId, success: false };
          }

          try {
            if (!userCommunication.userEmailAddress) {
              throw new Error('No user email address found in communication');
            }

            if (!userCommunication.platform) {
              throw new Error('No platform found in communication');
            }

            await modifyEmail({
              userEmailAddress: userCommunication.userEmailAddress,
              platform: userCommunication.platform,
              messageId: userCommunication.messageId,
              action: EmailRequestType.READ,
            });

            return { messageId, success: true };
          } catch (error) {
            log.error(
              `Failed to mark email as read for messageId ${messageId}:`,
              error,
            );
            return { messageId, success: false, error };
          }
        });

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);

        if (failed.length > 0) {
          const failedMessageIds = failed.map((result) => result.messageId);
          failed.forEach((result) => {
            unReadUserCommunication(result.messageId);
          });

          unreadUserCommunicationsInInboxEntries(failedMessageIds);

          log.warn(
            `${failed.length} operations failed out of ${validCommunications.length}`,
          );

          errorMessage(
            failed.length === validCommunications.length
              ? t('inbox.userCommunication.actions.markAsReadError')
              : t('inbox.userCommunication.actions.partialMarkAsReadError', {
                  failed: failed.length,
                  total: validCommunications.length,
                }),
          );
        }
      } catch (error) {
        validCommunications.forEach((messageId) => {
          unReadUserCommunication(messageId);
        });

        unreadUserCommunicationsInInboxEntries(validCommunications);

        log.error(error);
        errorMessage(t('inbox.userCommunication.actions.markAsReadError'));
      } finally {
        setMarkAsReadLoading(false);
      }
    },
    [errorMessage, t],
  );

  const extractMessageIdFromSelectedItems = useCallback(
    (selectedItems: Set<string>) => {
      return Array.from(selectedItems).map((messageId) => messageId);
    },
    [],
  );

  return {
    handleMarkAsReadUserCommunication,
    extractMessageIdFromSelectedItems,
  };
};
